import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Send, Plus, Search, MessageCircle, Users, ChevronRight, ShieldCheck, Download, Trash2, Pencil, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../utils/constants';
import { useUser } from '../context/UserContext';
import { useTheme, useThemeClasses } from '../hooks/useTheme';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';

export default function ChatPage() {
  const { user } = useUser();
  const { isDark } = useTheme();
  const theme = useThemeClasses(isDark);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const connectionId = params.get('conversation') || '';
  const [conversations, setConversations] = useState([]);
  const [inboxLoading, setInboxLoading] = useState(true);
  const [inboxError, setInboxError] = useState('');
  const [search, setSearch] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState('');
  const [starting, setStarting] = useState('');
  const messageEnd = useRef(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState('');
  const [confirmErase, setConfirmErase] = useState(false);
  const pending = useRef(null);
  const visibleCount = useRef(50);
  const currentConnection = useRef(connectionId);
  currentConnection.current = connectionId;

  const fetchInbox = useCallback(async () => {
    const { data } = await axios.get(`${API}/chat/conversations`);
    setConversations(data);
    setInboxError('');
  }, []);

  useEffect(() => {
    let active = true;
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/chat/conversations`);
        if (active) { setConversations(data); setInboxError(''); }
      } catch {
        if (active) setInboxError('Could not load conversations. Please retry.');
      } finally {
        if (active) setInboxLoading(false);
      }
    };
    fetch();
    const interval = setInterval(() => { if (document.visibilityState === 'visible') fetch(); }, 5000);
    return () => { active = false; clearInterval(interval); };
  }, []);

  useEffect(() => {
    if (!showNewChat) return;
    let active = true;
    setContactsLoading(true); setContactsError(''); setContactSearch('');
    axios.get(`${API}/chat/contacts`).then(({ data }) => {
      if (active) setContacts(data);
    }).catch(() => { if (active) setContactsError('Could not load your contacts. Close this window and retry.'); })
      .finally(() => { if (active) setContactsLoading(false); });
    return () => { active = false; };
  }, [showNewChat]);

  const startChat = async contact => {
    if (starting) return;
    setStarting(contact.id);
    try {
      const { data } = await axios.post(`${API}/chat/conversations`, contact.start);
      setConversations(previous => [data, ...previous.filter(c => c.id !== data.id)]);
      setParams({ conversation: data.id });
      setShowNewChat(false);
      fetchInbox().catch(() => {});
    } catch { toast.error('Could not start this conversation. Please retry.'); }
    finally { setStarting(''); }
  };

  const refresh = useCallback(async () => {
    if (!connectionId) return;
    let before;
    let collected = [];
    let hasMorePages = false;
    do {
      const { data } = await axios.get(`${API}/chat/${connectionId}/messages`, { params: { before, limit: 50 } });
      if (currentConnection.current !== connectionId) return;
      collected = [...data.messages, ...collected];
      hasMorePages = data.has_more;
      before = data.messages[0]?.created_at;
    } while (hasMorePages && before && collected.length < visibleCount.current);
    setMessages(collected);
    setHasMore(hasMorePages);
    setError('');
  }, [connectionId]);

  useEffect(() => {
    setMessages([]); setDraft(''); setEditing(null); setHasMore(false); setError(''); pending.current = null; visibleCount.current = 50;
    if (!connectionId) { setLoading(false); return; }
    setLoading(true);
    let active = true;
    const fetch = () => refresh().catch(() => {
      if (active && currentConnection.current === connectionId) { setMessages([]); setError('Conversation unavailable. Your connection may have ended, or you may need to sign in again.'); }
    }).finally(() => { if (active && currentConnection.current === connectionId) setLoading(false); });
    fetch();
    const interval = setInterval(() => { if (document.visibilityState === 'visible') fetch(); }, 5000);
    return () => { active = false; clearInterval(interval); };
  }, [connectionId, refresh]); // Poll only while this conversation is open.

  const loadOlder = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/chat/${connectionId}/messages`, { params: { before: messages[0]?.created_at } });
      if (currentConnection.current !== connectionId) return;
      setMessages(previous => [...data.messages, ...previous.filter(m => !data.messages.some(row => row.id === m.id))]);
      visibleCount.current += data.messages.length;
      setHasMore(data.has_more);
    } catch { toast.error('Could not load older messages'); }
    finally { setBusy(false); }
  };

  const send = async e => {
    e.preventDefault();
    if (!draft.trim() || busy) return;
    setBusy(true);
    const text = draft.trim();
    if (!pending.current || pending.current.text !== text) pending.current = { text, request_id: crypto.randomUUID() };
    try {
      await axios.post(`${API}/chat/${connectionId}/messages`, pending.current);
      if (currentConnection.current !== connectionId) return;
      setDraft(''); pending.current = null;
      await refresh();
      messageEnd.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      fetchInbox().catch(() => {});
    } catch { toast.error('Message could not be sent. Your text is still here; retry.'); }
    finally { setBusy(false); }
  };

  const remove = async message => {
    setBusy(true);
    try {
      await axios.delete(`${API}/chat/${connectionId}/messages/${message.id}`);
      setMessages(previous => previous.filter(m => m.id !== message.id));
      toast.success('Message deleted for all participants');
    } catch { toast.error('Could not delete message'); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!editText.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/chat/${connectionId}/messages/${editing}`, { text: editText.trim() });
      setMessages(previous => previous.map(m => m.id === editing ? data : m)); setEditing(null);
    } catch { toast.error('Could not update message'); }
    finally { setBusy(false); }
  };

  const exportOwn = async () => {
    try {
      const { data } = await axios.get(`${API}/chat/export`);
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
      const a = document.createElement('a'); a.href = url; a.download = 'mygoal-my-messages.json'; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error('Could not export messages'); }
  };

  const eraseOwn = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/chat/messages`);
      setMessages(previous => previous.filter(m => m.sender_id !== user.id));
      setConfirmErase(false); toast.success('Your messages were deleted from all conversations');
    } catch { toast.error('Could not delete your messages'); }
    finally { setBusy(false); }
  };

  const selected = conversations.find(c => c.id === connectionId);
  const filtered = conversations.filter(c => `${c.name} ${c.group_name || ''}`.toLowerCase().includes(search.toLowerCase()));
  const filteredContacts = contacts.filter(c => `${c.name} ${c.group_name || ''}`.toLowerCase().includes(contactSearch.toLowerCase()));
  const initials = name => name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();
  const primaryButton = 'bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white rounded-xl font-semibold transition-opacity hover:opacity-90 disabled:opacity-50';

  return <div className={`min-h-screen ${theme.bg} ${theme.textPrimary} transition-colors duration-300 pb-24`} data-testid="page-chat">
    <header className="flex items-center justify-between p-4 pt-6 max-w-6xl mx-auto">
      <button aria-label="Back to home" onClick={() => navigate('/')} className={`w-10 h-10 rounded-full flex items-center justify-center ${theme.button}`}><ArrowLeft size={20} /></button>
      <h1 className="text-xl font-bold">Messages</h1>
      <button aria-label="Start a new chat" onClick={() => setShowNewChat(true)} className={`w-10 h-10 flex items-center justify-center !rounded-full ${primaryButton}`}><Plus size={20} /></button>
    </header>
    <div className="max-w-6xl mx-auto px-4">
      <p className={`text-sm mb-5 ${theme.textSecondary}`}>Stay connected with your {user.account_type === 'coach' ? 'players' : 'coaches'}. Your activity messages stay in the same conversation.</p>
      <div className="grid md:grid-cols-[320px_minmax(0,1fr)] gap-4">
        <aside className={`${connectionId ? 'hidden md:flex' : 'flex'} flex-col rounded-3xl border overflow-hidden ${theme.card}`} aria-label="Conversations">
          <div className="p-4 border-b border-current/10">
            <div className="flex items-center justify-between mb-4"><h2 className="font-bold">Your conversations</h2><span className={`text-xs px-2 py-1 rounded-full ${theme.button}`}>{conversations.length}</span></div>
            <label className={`flex items-center gap-2 border rounded-xl px-3 ${theme.input}`}>
              <Search size={16} aria-hidden="true" />
              <input aria-label="Search conversations" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search conversations" className="w-full bg-transparent py-3 text-sm outline-none" />
            </label>
          </div>
          <div className="flex-1 overflow-y-auto md:max-h-[60vh] p-2">
            {inboxLoading ? <p className={`text-center p-8 ${theme.textMuted}`}>Loading conversations…</p> : inboxError ? <div role="alert" className="p-4 text-sm"><p>{inboxError}</p><button onClick={() => fetchInbox().catch(() => {})} className="underline mt-2">Retry</button></div> : !filtered.length ? <div className="text-center px-4 py-10">
              <MessageCircle size={40} className={`mx-auto mb-3 ${theme.textMuted}`} />
              <p className="font-semibold">{search ? 'No conversations found' : 'Your conversations start here'}</p>
              <p className={`text-sm mt-2 ${theme.textMuted}`}>{search ? 'Try a different name.' : 'Start a chat or send a message with your next activity rating.'}</p>
            </div> : filtered.map(c => <button key={c.id} onClick={() => setParams({ conversation: c.id })} className={`w-full flex items-center gap-3 p-3 rounded-2xl text-left mb-1 transition-colors ${c.id === connectionId ? (isDark ? 'bg-[#493986]/40' : 'bg-[#493986]/10') : theme.button}`}>
              <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#493986] to-[#92a7e8] text-white flex items-center justify-center shrink-0 text-sm font-bold">{c.kind === 'group' ? <Users size={20} /> : initials(c.name)}</span>
              <span className="min-w-0 flex-1"><span className="block font-semibold truncate text-sm">{c.name}</span><span className={`block text-xs truncate mt-1 ${theme.textMuted}`}>{c.kind === 'group' ? c.group_name : 'Private conversation'}</span></span>
              <ChevronRight size={16} className={theme.textMuted} />
            </button>)}
          </div>
          <div className="p-4"><button onClick={() => setShowNewChat(true)} className={`w-full flex items-center justify-center gap-2 py-3 ${primaryButton}`}><Plus size={18} />New chat</button></div>
        </aside>
        <section className={`${connectionId ? 'flex' : 'hidden md:flex'} flex-col min-w-0 rounded-3xl border overflow-hidden ${theme.card} h-[72dvh] min-h-[420px]`} aria-label="Current conversation">
          {!connectionId ? <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-[#493986] to-[#92a7e8] flex items-center justify-center text-white mb-5"><MessageCircle size={36} /></div>
            <h2 className="text-xl font-bold">A conversation that keeps going</h2>
            <p className={`text-sm max-w-sm mt-3 mb-6 ${theme.textMuted}`}>Ask a question, share how training went or follow up on an activity. Everything stays together with the right coaches.</p>
            <button onClick={() => setShowNewChat(true)} className={`px-6 py-3 flex items-center gap-2 ${primaryButton}`}><Plus size={18} />Start a new chat</button>
          </div> : <>
            <div className="p-4 flex items-center gap-3 border-b border-current/10">
              <button aria-label="Back to conversations" onClick={() => setParams({})} className={`md:hidden p-2 rounded-full ${theme.button}`}><ArrowLeft size={20} /></button>
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#493986] to-[#92a7e8] text-white flex items-center justify-center shrink-0">{selected?.kind === 'group' ? <Users size={20} /> : <MessageCircle size={20} />}</div>
              <div className="min-w-0"><h2 className="font-bold truncate">{selected?.name || 'Conversation'}</h2><p className={`text-xs mt-1 ${theme.textMuted}`}>{selected?.kind === 'group' ? selected.group_name : 'Private conversation'}</p></div>
            </div>
            {selected?.participants && <p className={`px-4 py-2 text-xs border-b border-current/10 ${theme.textMuted}`}>{selected.participants.map(p => p.name).join(' · ')}</p>}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" aria-label="Messages" aria-live="polite" aria-relevant="additions">
              {loading ? <p className={`text-center py-8 ${theme.textMuted}`}>Loading messages…</p> : error ? <div role="alert" className="text-sm text-center py-8"><p>{error}</p><button onClick={() => refresh().catch(() => {})} className="underline mt-2">Retry</button></div> : <>
                {hasMore && <button disabled={busy} onClick={loadOlder} className={`block mx-auto px-4 py-2 text-xs rounded-full ${theme.button}`}>Load older messages</button>}
                {!messages.length && <div className="text-center py-10"><MessageCircle size={32} className={`mx-auto mb-3 ${theme.textMuted}`} /><p className="font-semibold">Say hello</p><p className={`text-sm mt-2 ${theme.textMuted}`}>Send the first message to start your conversation.</p></div>}
                {messages.map(m => <article key={m.id} className={`max-w-[88%] sm:max-w-[80%] rounded-2xl p-4 ${m.sender_id === user.id ? 'ml-auto bg-gradient-to-br from-[#493986] to-[#65559c] text-white rounded-br-md' : `mr-auto border ${theme.card} rounded-bl-md`}`}>
                  <p className="font-semibold text-xs mb-2">{m.sender_id === user.id ? 'You' : m.sender_name}</p>
                  {m.activity && <div className={`text-xs px-3 py-2 rounded-xl mb-2 ${m.sender_id === user.id ? 'bg-white/10' : theme.button}`}><span className="block opacity-60 mb-1">Activity feedback</span>{m.activity.name}</div>}
                  {editing === m.id ? <>
                    <textarea aria-label="Edit your message" rows={3} maxLength={2000} disabled={busy} value={editText} onChange={e => setEditText(e.target.value)} className={`w-full border rounded-xl p-2 ${theme.input}`} />
                    <button disabled={busy || !editText.trim()} onClick={saveEdit} className="p-2 text-xs underline">Save</button><button disabled={busy} onClick={() => setEditing(null)} className="p-2 text-xs">Cancel</button>
                  </> : <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{m.text}</p>}
                  <div className="flex items-center justify-between gap-3 mt-3 text-[11px] opacity-70"><time dateTime={m.created_at}>{new Date(m.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}{m.edited_at ? ' · edited' : ''}</time>
                    {m.sender_id === user.id && <div className="flex gap-3"><button aria-label="Edit message" disabled={busy} onClick={() => { setEditing(m.id); setEditText(m.text); }}><Pencil size={13} /></button><button aria-label="Delete message" disabled={busy} onClick={() => remove(m)}><Trash2 size={13} /></button></div>}
                  </div>
                </article>)}
                <div ref={messageEnd} />
              </>}
            </div>
            <form onSubmit={send} className="p-4 border-t border-current/10">
              <label htmlFor="chat-text" className="sr-only">Your message</label>
              <div className={`flex items-end gap-2 border rounded-2xl p-2 ${theme.input}`}>
                <textarea id="chat-text" maxLength={2000} rows={2} disabled={busy || !!error || loading} value={draft} onChange={e => setDraft(e.target.value)} className="flex-1 min-w-0 bg-transparent p-2 text-sm resize-none outline-none" placeholder="Write a message…" />
                <button aria-label="Send message" type="submit" disabled={busy || !draft.trim() || !!error || loading} className={`p-3 shrink-0 ${primaryButton}`}>{busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}</button>
              </div>
              <p className={`text-[11px] text-right mt-2 ${theme.textMuted}`}>{draft.length}/2000</p>
            </form>
          </>}
        </section>
      </div>
      <details className={`mt-5 text-xs ${theme.textMuted}`}>
        <summary className="cursor-pointer flex items-center gap-2 py-2"><ShieldCheck size={14} />Privacy & message settings</summary>
        <p className="mt-2 leading-relaxed max-w-3xl">Your name is visible in chat. Group conversations include the player and the coaches assigned to that group. Rating scores stay out of chat. Messages are not used in analytics or AI and expire after one year. You can edit, delete or export your own messages. Contact <a className="underline" href="mailto:privacy@mygoalapp.com">privacy@mygoalapp.com</a> for privacy requests.</p>
        <div className="flex gap-4 mt-3"><button onClick={exportOwn} className="flex items-center gap-1 underline"><Download size={13} />Export my messages</button><button onClick={() => setConfirmErase(true)} className="flex items-center gap-1 underline"><Trash2 size={13} />Delete all my messages</button></div>
      </details>
    </div>
    <Dialog open={showNewChat} onOpenChange={setShowNewChat}>
      <DialogContent className={`rounded-3xl border p-6 ${theme.card} ${theme.textPrimary}`}>
        <DialogTitle className="text-xl font-bold">Start a new chat</DialogTitle>
        <DialogDescription className={theme.textMuted}>Choose a connected {user.account_type === 'coach' ? 'player' : 'coach'} or your coaching group. An existing conversation will open automatically.</DialogDescription>
        <label className={`flex items-center gap-2 border rounded-xl px-3 ${theme.input}`}><Search size={16} /><input autoFocus aria-label="Search chat contacts" value={contactSearch} onChange={e => setContactSearch(e.target.value)} placeholder="Search by name or group" className="w-full bg-transparent py-3 text-sm outline-none" /></label>
        <div className="max-h-[45vh] overflow-y-auto space-y-2">
          {contactsLoading ? <p className={`text-center py-8 ${theme.textMuted}`}>Loading contacts…</p> : contactsError ? <p role="alert" className="py-6 text-sm">{contactsError}</p> : !filteredContacts.length ? <div className="text-center py-8"><Users size={32} className={`mx-auto mb-3 ${theme.textMuted}`} /><p className="font-semibold">{contactSearch ? 'No contacts found' : 'No connected contacts yet'}</p><p className={`text-sm mt-2 ${theme.textMuted}`}>{contactSearch ? 'Try a different name.' : 'Connect with a player or coach, or join a coaching group first.'}</p></div> : filteredContacts.map(c => <button key={c.id} disabled={!!starting} onClick={() => startChat(c)} className={`w-full flex items-center gap-3 p-3 rounded-2xl text-left ${theme.button} disabled:opacity-50`}>
            <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#493986] to-[#92a7e8] text-white flex items-center justify-center shrink-0 text-sm font-bold">{c.kind === 'group' ? <Users size={20} /> : initials(c.name)}</span>
            <span className="flex-1 min-w-0"><span className="block font-semibold text-sm truncate">{c.name}</span><span className={`block text-xs mt-1 ${theme.textMuted}`}>{c.kind === 'group' ? c.group_name : 'Private conversation'}</span></span>
            {starting === c.id ? <Loader2 size={18} className="animate-spin" /> : <ChevronRight size={18} />}
          </button>)}
        </div>
      </DialogContent>
    </Dialog>
    <Dialog open={confirmErase} onOpenChange={setConfirmErase}>
      <DialogContent className={`rounded-3xl border p-6 ${theme.card} ${theme.textPrimary}`}>
        <DialogTitle>Delete all your messages?</DialogTitle>
        <DialogDescription className={theme.textMuted}>This removes every message you sent from all participants’ histories. This cannot be undone.</DialogDescription>
        <div className="flex justify-end gap-2"><button disabled={busy} onClick={() => setConfirmErase(false)} className={`px-4 py-3 rounded-xl ${theme.button}`}>Cancel</button><button disabled={busy} onClick={eraseOwn} className="px-4 py-3 rounded-xl bg-red-500 text-white font-semibold">Delete my messages</button></div>
      </DialogContent>
    </Dialog>
  </div>;
}
